/**
 * Admin — Security/Session & Access
 *
 * Three sections — all wired to the real /api/v1/admin/security/* surface
 * added in Wave 1b Stage 2 (b36 schema):
 *
 *   1. Session policy   — GET /admin/security/policy + PUT /admin/security/session-policy
 *   2. IP zones         — GET/POST /admin/security/ip-zones, PUT/DELETE /admin/security/ip-zones/:id,
 *                          POST /admin/security/ip-zones/reorder
 *   3. Rate-limit tier  — PUT /admin/security/rate-limit-tier (current value via /policy)
 *
 * The richer mock-data UI (multi-CIDR zones, allow/deny/require_mfa/admin_only,
 * per-tier rpm/rph/rpd/burst editing) is preserved as a v1.1 polish item;
 * this surface persists exactly the fields the b36 schema supports today
 * and shows nothing it cannot save. Anything not yet wired is rendered as
 * read-only with an explanatory note rather than a fake save.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  Clock, Globe, Gauge, Plus, Trash2, ArrowUp, ArrowDown, Loader2,
  AlertTriangle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { SectionCard, SettingField } from '@/components/admin';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/security/session')({
  component: SessionPage,
});

// ─── Types (mirror server response shapes) ───────────────────────────────

interface SessionPolicy {
  idleTimeoutMinutes: number;
  absoluteTimeoutHours: number;
  refreshRotation: boolean;
  concurrentSessionLimit: number;
}

type RateLimitTier =
  | 'unlimited'
  | 'high'
  | 'standard'
  | 'low'
  | 'restricted'
  | 'custom';

interface IpZone {
  id: string;
  name: string;
  cidr: string;
  action: 'allow' | 'deny';
  sortOrder: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PolicyResponse {
  passwordPolicy: unknown;
  mfaConfig: unknown;
  sessionPolicy: SessionPolicy;
  rateLimitTier: RateLimitTier;
  rateLimitCustom: unknown;
  updatedBy: string | null;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page
// ═══════════════════════════════════════════════════════════════════════════

function SessionPage() {
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await apiClient.get<PolicyResponse>(
        '/admin/security/policy',
      );
      setPolicy(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setLoadError(msg);
      toast.error('Failed to load security policy');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Session & Access</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Session lifetime, network zones, and API rate limits.
        </p>
      </div>

      {loading || !policy ? (
        loadError ? (
          <Card>
            <CardContent className="flex items-center gap-2 py-6 text-xs text-red-600">
              <AlertTriangle className="h-4 w-4" />
              Failed to load security policy: {loadError}
            </CardContent>
          </Card>
        ) : (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading session policy…
          </div>
        )
      ) : (
        <>
          <SessionPolicySection
            value={policy.sessionPolicy}
            onSaved={(next) =>
              setPolicy((p) => (p ? { ...p, sessionPolicy: next } : p))
            }
          />
          <RateLimitSection
            tier={policy.rateLimitTier}
            onSaved={(t) =>
              setPolicy((p) => (p ? { ...p, rateLimitTier: t } : p))
            }
          />
          <IpZonesSection />
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Session Policy
// ═══════════════════════════════════════════════════════════════════════════

function SessionPolicySection({
  value, onSaved,
}: {
  value: SessionPolicy;
  onSaved: (next: SessionPolicy) => void;
}) {
  const [draft, setDraft] = useState<SessionPolicy>(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value), [value]);

  async function save() {
    if (draft.idleTimeoutMinutes < 10) {
      toast.error('Idle timeout must be at least 10 minutes.');
      return;
    }
    if (draft.idleTimeoutMinutes > draft.absoluteTimeoutHours * 60) {
      toast.error(
        'Idle timeout cannot exceed absolute timeout (otherwise idle never triggers).',
      );
      return;
    }
    try {
      setSaving(true);
      const res = await apiClient.put<{
        success: true;
        sessionPolicy: SessionPolicy;
      }>('/admin/security/session-policy', draft);
      onSaved(res.sessionPolicy);
      toast.success('Session policy updated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Session policy"
      icon={Clock}
      description="Token TTLs and session timeout. HIPAA §164.312(a)(2)(iii) requires automatic logoff after a period of inactivity."
      actions={
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        <SettingField
          label="Idle timeout (minutes)"
          description="Auto-logout after inactivity. HIPAA §164.312(a)(2)(iii)."
        >
          <Input
            type="number"
            value={draft.idleTimeoutMinutes}
            onChange={(e) =>
              setDraft({
                ...draft,
                idleTimeoutMinutes: parseInt(e.target.value, 10) || 60,
              })
            }
            className="h-8 w-24 text-xs tabular-nums"
            min={10}
            max={1440}
          />
        </SettingField>

        <SettingField
          label="Absolute timeout (hours)"
          description="Hard cap regardless of activity."
        >
          <Input
            type="number"
            value={draft.absoluteTimeoutHours}
            onChange={(e) =>
              setDraft({
                ...draft,
                absoluteTimeoutHours: parseInt(e.target.value, 10) || 24,
              })
            }
            className="h-8 w-24 text-xs tabular-nums"
            min={1}
            max={720}
          />
        </SettingField>

        <SettingField
          label="Refresh-token rotation"
          description="Issue a new refresh token on every refresh."
        >
          <Switch
            checked={draft.refreshRotation}
            onCheckedChange={(v) =>
              setDraft({ ...draft, refreshRotation: v })
            }
            className="scale-75"
          />
        </SettingField>

        <SettingField
          label="Concurrent sessions per user"
          description="0 = unlimited. Low values reduce credential-sharing risk."
        >
          <Input
            type="number"
            value={draft.concurrentSessionLimit}
            onChange={(e) =>
              setDraft({
                ...draft,
                concurrentSessionLimit: parseInt(e.target.value, 10) || 0,
              })
            }
            className="h-8 w-24 text-xs tabular-nums"
            min={0}
            max={100}
          />
        </SettingField>
      </div>
    </SectionCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Rate-limit tier
// ═══════════════════════════════════════════════════════════════════════════

function RateLimitSection({
  tier, onSaved,
}: {
  tier: RateLimitTier;
  onSaved: (next: RateLimitTier) => void;
}) {
  const [draft, setDraft] = useState<RateLimitTier>(tier);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(tier), [tier]);

  async function save() {
    try {
      setSaving(true);
      await apiClient.put('/admin/security/rate-limit-tier', { tier: draft });
      onSaved(draft);
      toast.success(`Rate-limit tier set to ${draft}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Rate-limit tier"
      icon={Gauge}
      description="5 built-in tiers + custom. Per-tier rpm/rph/rpd/burst editing arrives in v1.1; enforcement at request time arrives in Wave 2."
      actions={
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <SettingField
        label="Tier"
        description="Higher tiers grant more headroom; restricted is for paused tenants."
      >
        <Select value={draft} onValueChange={(v) => setDraft(v as RateLimitTier)}>
          <SelectTrigger className="h-8 w-48 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unlimited" className="text-xs">Unlimited</SelectItem>
            <SelectItem value="high" className="text-xs">High</SelectItem>
            <SelectItem value="standard" className="text-xs">Standard (default)</SelectItem>
            <SelectItem value="low" className="text-xs">Low</SelectItem>
            <SelectItem value="restricted" className="text-xs">Restricted</SelectItem>
            <SelectItem value="custom" className="text-xs">Custom</SelectItem>
          </SelectContent>
        </Select>
      </SettingField>
    </SectionCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  IP Zones
// ═══════════════════════════════════════════════════════════════════════════

function IpZonesSection() {
  const [zones, setZones] = useState<IpZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await apiClient.get<{ zones: IpZone[] }>(
        '/admin/security/ip-zones',
      );
      setZones(data.zones);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setLoadError(msg);
      toast.error('Failed to load IP zones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function move(id: string, dir: -1 | 1) {
    const idx = zones.findIndex((z) => z.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= zones.length) return;
    const next = [...zones];
    [next[idx], next[target]] = [next[target], next[idx]];
    setZones(next); // optimistic
    try {
      await apiClient.post('/admin/security/ip-zones/reorder', {
        order: next.map((z) => z.id),
      });
      void refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reorder';
      toast.error(msg);
      void refresh();
    }
  }

  async function deleteZone(id: string, name: string) {
    if (!window.confirm(`Remove IP zone "${name}"?`)) return;
    try {
      await apiClient.delete(`/admin/security/ip-zones/${id}`);
      toast.success('Zone removed');
      void refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="IP allow / deny zones"
      icon={Globe}
      description="Ordered list, first match wins by sort order. Enforcement at request time is Wave 2/3."
      actions={
        <Button
          size="sm"
          className="h-7 text-xs gap-1.5"
          onClick={() => setAdding(true)}
        >
          <Plus className="h-3 w-3" />
          Add zone
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading zones…
        </div>
      ) : loadError ? (
        <div className="flex items-center gap-2 py-3 text-xs text-red-600">
          <AlertTriangle className="h-4 w-4" />
          Failed to load IP zones: {loadError}
        </div>
      ) : zones.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-md">
          <Globe className="h-5 w-5 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No IP zones configured</div>
          <p className="text-[10px] text-muted-foreground mt-1 max-w-xs">
            All addresses are allowed (default-allow when no zones exist).
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {zones.map((z, i) => (
            <div
              key={z.id}
              className="flex items-center gap-2 border rounded-md p-2.5"
            >
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  disabled={i === 0}
                  onClick={() => move(z.id, -1)}
                  title="Move up"
                >
                  <ArrowUp className="h-2.5 w-2.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  disabled={i === zones.length - 1}
                  onClick={() => move(z.id, 1)}
                  title="Move down"
                >
                  <ArrowDown className="h-2.5 w-2.5" />
                </Button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium">{z.name}</span>
                  <Badge
                    variant="outline"
                    className={
                      z.action === 'allow'
                        ? 'h-4 text-[9px] px-1.5 border-emerald-500/40 text-emerald-600'
                        : 'h-4 text-[9px] px-1.5 border-red-500/40 text-red-600'
                    }
                  >
                    {z.action}
                  </Badge>
                  <code className="text-[10px] font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                    {z.cidr}
                  </code>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                  Sort {z.sortOrder}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => deleteZone(z.id, z.name)}
                title="Remove zone"
              >
                <Trash2 className="h-3 w-3 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <AddIpZoneDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void refresh();
          }}
        />
      )}
    </SectionCard>
  );
}

function AddIpZoneDialog({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [cidr, setCidr] = useState('');
  const [action, setAction] = useState<'allow' | 'deny'>('allow');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!name.trim() || !cidr.trim()) {
      toast.error('Name and CIDR required');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/ip-zones', {
        name: name.trim(),
        cidr: cidr.trim(),
        action,
        isEnabled: true,
      });
      toast.success('Zone added');
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-background border rounded-lg p-5 w-[420px] space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold">Add IP zone</div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Office VPN"
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            CIDR
          </Label>
          <Input
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            placeholder="10.0.0.0/8 or 2001:db8::/32"
            className="h-8 text-xs font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Action
          </Label>
          <Select value={action} onValueChange={(v) => setAction(v as 'allow' | 'deny')}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="allow" className="text-xs">Allow</SelectItem>
              <SelectItem value="deny" className="text-xs">Deny</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}
